import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AlertCircle, ImagePlus, Images, Loader2, Lock, Plus } from 'lucide-react';
import Button from '../../../ui/Button';
import Popup from '../../../ui/Popup';
import WizardShell from '../../../ui/WizardShell';
import BottomBar from '../../../ui/BottomBar';
import BookSidePanel from '../../../ui/BookSidePanel';
import Skeleton, { SkeletonGroup } from '../../../ui/Skeleton';
import PhotoGrid from '../../../builder/PhotoGrid';
import PageCropScreen from '../../../builder/PageCropScreen';
import PickerHeadsUp from '../../../builder/PickerHeadsUp';
import { CoverStatusStrip, CoverReadySheet, CoverDrawFailedBox } from '../../../builder/CoverStatusStrip';
import { useCoverRetry } from '../../../builder/useCoverRetry';
import { useBuilderBack, useBuilderBusy, useBuilderStepInterceptor } from '../../../builder/BuilderNavContext';
import { BASE_BOOK_PRICE } from '../../../../config/pricing';
import { isEtsyUser } from '../../../../services/etsyService';
import { toast } from 'react-toastify';
import { dismissMessageBar, showMessageBar } from '../../../../util/messageBar';
import { applePickerDevice, markPickerHeadsUpDone, shouldShowPickerHeadsUp } from '../../../../util/photoPickerHeadsUp';
import { FUNNEL_EVENTS, pushFunnelEvent } from '../../../../util/funnelEvents';
import {
  BUILDER_ACTIONS,
  BUILDER_STEPS,
  countBuilderAction,
  useBuilderStepVisit,
  useCropWindowTiming,
} from '../../../../util/builderStepTiming';
import { resolveBookTag } from '../../../../util/bookTag';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteImage,
  setBatchId,
  setCoverImages,
  setImages,
} from '../../../../redux/slice/sketchSlice';
import {
  initialLineartProgress,
  setActiveBookId,
  setForceBookRefresh,
  setLineartProgressByBook,
  setShouldWarmupLineart,
  setUploadingImages,
  clearUploadingImages,
} from '../../../../redux/slice/stepSlice';
import { saveStep } from '../../../../services/stepService';

import BookSelectionModal from '../../../modalcontent/BookSelectionModal';
import {
  removeBookImage,
} from '../../../../services/bookService';
import FileUploader from '../../../common/UppyUploader';
import { markStepAsVisited } from '../../../../redux/slice/profileSlice';
import { run, warmup } from '../../../../services/runpodService';
import { processAllImagesForRun, autoCropImageWithRect, cropCacheKey, CROP_SAVE_FAILED_MESSAGE, isSmartCropEnabled, recutCropFromData, uploadUserImage } from '../../../../util/imageCropUploadUtils';
import { warmFaceDetector } from '../../../../util/faceDetector';
import { buildCropUploadRef, isStagingUploadEnabled } from '../../../../services/stagingUploadService';
import { savePendingImages, loadPendingImages, loadPendingCoverImage, removePendingImage, markImageAsSentToRun, clearSentToRunForImage, getCroppedCloudinaryDisplayUrl, hydratePendingImageForGrid, setExpectedInteriorPageCount, reconcileStaleSentToRunQueue, hasImagesSentToRunPending } from '../../../../util/pendingImageStorage';
import {
  allImagesGenerated,
  tagUploadableWithGridOrder,
  getImageKey,
  getMediaMongoId,
  isMongoMediaId,
  getUploadableImages,
  getImagesMissingLineart,
  isLineartGenerationActive,
  isImageGenerated,
  isChoosePhotosNextBlockedByCropPrep,
  isPhotoCropPrepInProgress,
  needsCropSave,
} from '../../../../util/placeOrderImageUtils';
import {
  fetchOrderedInteriorImages,
  persistInteriorPageOrder,
  sortByPageOrdering,
} from '../../../../util/bookPageOrderUtils';
import { preserveInMemoryGridUploads, reconcileIncomingUploads } from '../../../../util/imageDedup';
import useBookPagesFromServer from '../../../../hooks/useBookPagesFromServer';
import { lineartStatusFromRows, LINEART_STAGE_STARTING } from '../../../../util/previewInteriorGridUtils';
import { markLineartRunFinished } from '../../../../util/lineartRunFinished';
import WorthALookNotice from './WorthALookNotice';
import { notifySaved } from '../../../../util/saveTracker';
import CoverPreview from '../../../builder/CoverPreview';

/**
 * The book and photos this step last had (after its order sync, and when it
 * closes). Module-level so it outlives the step: coming back from another step
 * of the same book shows the grid at once, and the order sync still refreshes
 * it in the background.
 */
let lastLoadedPhotos = { bookId: null, keys: null };

const rememberLoadedPhotos = (bookId, list) => {
  lastLoadedPhotos = { bookId, keys: new Set((list || []).map(getImageKey).filter(Boolean)) };
};

/**
 * Whether the photos in the store are still the ones this step last had for
 * this book. Opening another book from My Books only replaces them when that
 * book has photos, so a matching book id alone could show another book's grid.
 */
const photosStillLoaded = (bookId, list) => {
  if (!bookId || lastLoadedPhotos.bookId !== bookId || !lastLoadedPhotos.keys) return false;
  const keys = (list || []).map(getImageKey);
  if (keys.length === 0) return lastLoadedPhotos.keys.size === 0;
  return keys.every((key) => key && lastLoadedPhotos.keys.has(key));
};

/** Most a stalled photo-list request may hold the step on its loading state. */
const MAX_PHOTO_LOAD_WAIT_MS = 5000;

/** A crop save is tried this many times, waiting these gaps between tries, before it counts as failed. */
const CROP_SAVE_TRIES = 3;
const CROP_SAVE_RETRY_DELAYS_MS = [2000, 5000];

const PlaceOrderContent = () => {
  const [croppingImage, setCroppingImage] = useState(null);
  const [deletedFilenames, setDeletedFilenames] = useState([]);
  const [isOpenBookSelectionModal, setIsOpenBookSelectionModal] =
    useState(false);
  const [showImageLimitModal, setShowImageLimitModal] = useState(false);
  const [showLessThan20Modal, setShowLessThan20Modal] = useState(false);
  // Photos picked but not in the grid yet: uploading (the uploader's count), then auto-cropping.
  const [uploaderBusy, setUploaderBusy] = useState(0);
  const [placingPicked, setPlacingPicked] = useState(false);
  // iPhone/iPad: explain the picker's silent wait before opening it, until photos have arrived once.
  const [pickerHeadsUpOpen, setPickerHeadsUpOpen] = useState(false);
  const [modalImageCount, setModalImageCount] = useState(0);
  const [blobUrlCache, setBlobUrlCache] = useState({}); // Cache blob URLs
  /** Prevents double Next / double RunPod while a job is starting. */
  const generationInFlightRef = useRef(false);
  // Deletes on their way to the server. Next waits for these: /run counts the
  // rows the server still has, not the grid.
  const pendingPhotoDeletesRef = useRef(new Set());
  // Photos gone from the grid that the server wouldn't delete. Next tries them
  // again, and stays on this step until they're gone.
  const failedPhotoDeletesRef = useRef(new Set());
  const goOnAfterDeleteFailedRef = useRef(null);
  // Photos removed on this visit. A grid refresh can be working from rows read
  // before the delete landed, and it mustn't bring these back.
  const removedPhotoIdsRef = useRef(new Set());
  const withoutRemovedPhotos = (rows) =>
    rows.filter((img) => !removedPhotoIdsRef.current.has(String(getMediaMongoId(img))));
  const [showDeleteFailed, setShowDeleteFailed] = useState(false);
  const [isStartingGeneration, setIsStartingGeneration] = useState(false);
  const uploaderRef = useRef(null);
  const [coverSheetOpen, setCoverSheetOpen] = useState(false);
  // Set while a photo is being replaced from the crop screen: the next upload takes its place.
  const replaceTargetRef = useRef(null);
  // Photos removed from the grid but not yet from the server (the Undo bar is still up).
  const pendingRemovalsRef = useRef(new Map());
  const flushPendingRemovals = () => {
    pendingRemovalsRef.current.forEach((commit) => commit());
    pendingRemovalsRef.current.clear();
  };
  useEffect(() => {
    window.addEventListener('pagehide', flushPendingRemovals);
    return () => {
      flushPendingRemovals();
      // Undo can't put photos back on a step that's gone.
      dismissMessageBar();
      window.removeEventListener('pagehide', flushPendingRemovals);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag");
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile);
  const myBooks = profile?.myBooks ?? [];
  const images = useSelector((state) => state.sketch.images);
  const coverImages = useSelector((state) => state.sketch.coverImages);
  const batchId = useSelector((state) => state.sketch.batchId);
  const { activeBookId, uploadingImagesBatch, uploadingImages, shouldWarmupLineart, forceBookRefresh, lineartProgressByBook, uploadingCoverImage } = useSelector(
    (state) => state.stepFlow,
  );
  useBuilderStepVisit(BUILDER_STEPS.PHOTOS, activeBookId);
  // Analytics: how long the crop window is open on this step.
  useCropWindowTiming(!!croppingImage);
  const activeBook = myBooks.find(({ _id }) => _id === activeBookId);
  const lineartProgress = lineartProgressByBook[activeBookId];
  const pendingImagesForBook = activeBookId ? loadPendingImages(activeBookId) : [];
  // The book's pages as the server has them: the same source and refresh as
  // Preview, so the lock, the count and the unlock here match step 5. Polls
  // only while the photos are locked (the lock is worked out just below).
  const [pollServerPages, setPollServerPages] = useState(false);
  const pagesStatusFor = (rows) =>
    lineartStatusFromRows(rows, activeBookId, { activeBook, lineartProgress });
  const { rows: serverPages, isFresh: serverPagesFresh } = useBookPagesFromServer(activeBookId, {
    poll: pollServerPages,
    isDone: (rows) => pagesStatusFor(rows).finished,
    refreshKey: forceBookRefresh,
    restartKey: [lineartProgress?.isProcessing, lineartProgress?.currentStage].join('|'),
  });
  const isLineartLocked = isLineartGenerationActive({
    bookId: activeBookId,
    lineartProgress,
    activeBook,
    pendingImages: pendingImagesForBook,
    uploadingImages,
    images,
    serverRows: serverPagesFresh ? serverPages : undefined,
  });
  useEffect(() => {
    setPollServerPages(isLineartLocked);
  }, [isLineartLocked]);
  const pagesStatus = pagesStatusFor(serverPages);
  const { session } = useSelector((state) => state.authProvider);

  // Loading only until this book's photo list has actually come back (the order
  // sync below), not for a fixed time: it used to wait 0.8 s, or 2.5 s for a
  // book with no photos, on every visit.
  const [photosLoadedFor, setPhotosLoadedFor] = useState(() =>
    photosStillLoaded(activeBookId, images) ? activeBookId : null,
  );
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  const isInitialLoading = (!activeBookId || photosLoadedFor !== activeBookId) && !waitedTooLong;
  const [stagingUploadEnabled, setStagingUploadEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    isStagingUploadEnabled()
      .then((enabled) => {
        if (!cancelled) setStagingUploadEnabled(!!enabled);
      })
      .catch(() => {
        if (!cancelled) setStagingUploadEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isInitialLoading) return undefined;
    const timer = setTimeout(() => setWaitedTooLong(true), MAX_PHOTO_LOAD_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isInitialLoading]);

  const markPhotosLoaded = (bookId, list) => {
    rememberLoadedPhotos(bookId, list);
    setPhotosLoadedFor(bookId);
  };

  // Remember what the grid held when the step closes, so uploads and crops made
  // here still count as this book's photos on the way back.
  const gridOnCloseRef = useRef(images);
  gridOnCloseRef.current = images;
  const bookOnCloseRef = useRef(activeBookId);
  bookOnCloseRef.current = activeBookId;
  useEffect(
    () => () => {
      if (lastLoadedPhotos.bookId === bookOnCloseRef.current) {
        rememberLoadedPhotos(bookOnCloseRef.current, gridOnCloseRef.current);
      }
    },
    [],
  );

  // cold start 2 workers
  /* useEffect(() => {
    if (shouldWarmupLineart) {
      warmup({ workflowType: 'lineart' }, 2);
      dispatch(setShouldWarmupLineart(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldWarmupLineart]) */

  useEffect(() => {
    if (!batchId) {
      dispatch(setBatchId(uuidv4()));
    }
  }, [dispatch, batchId]);

  // Start fetching the face detector while the customer is still picking
  // photos, so it is ready by the time a batch needs cropping.
  useEffect(() => {
    isSmartCropEnabled().then((on) => { if (on) warmFaceDetector(); });
  }, []);

  // Sync interior page order from DB when entering this step or after reorder elsewhere.
  useEffect(() => {
    if (!activeBookId) return;

    let cancelled = false;

    fetchOrderedInteriorImages(activeBookId)
      .then((merged) => {
        if (cancelled) return;
        dispatch((dispatch, getState) => {
          const liveImages = getState().sketch?.images || [];
          const next = withoutRemovedPhotos(preserveInMemoryGridUploads(
            merged,
            liveImages,
            sortByPageOrdering,
          ));
          console.log(
            `[IMAGE_DEBUG] Choose Photos order sync — ${next.length} images`,
          );
          dispatch(setImages(next));
          markPhotosLoaded(activeBookId, next);
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to sync page order on Choose Photos:', error);
        const pending = loadPendingImages(activeBookId);
        if (pending.length > 0) {
          dispatch(setImages(pending.map(hydratePendingImageForGrid)));
        }
        // Not remembered: a failed load shouldn't make the next visit skip loading.
        setPhotosLoadedFor(activeBookId);
      });

    if (coverImages.length === 0) {
      const pendingCover = loadPendingCoverImage(activeBookId);
      if (pendingCover) {
        dispatch(setCoverImages([pendingCover]));
      }
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBookId, forceBookRefresh]);

  // Restore lineart progress from DB when returning mid-generation (e.g. from Preview).
  useEffect(() => {
    if (!activeBookId || !activeBook) return;

    const dbIsProcessing =
      activeBook.isProcessing === true &&
      activeBook.processingWorkflowType === 'lineart' &&
      (activeBook.processingPercentage ?? 0) < 100;

    if (dbIsProcessing && !lineartProgress?.isProcessing) {
      dispatch(
        setLineartProgressByBook({
          bookId: activeBookId,
          isProcessing: true,
          overallPercentage: activeBook.processingPercentage || 0,
        }),
      );
    }
  }, [activeBookId, activeBook, lineartProgress?.isProcessing, dispatch]);

  // Drop stale sentToRun / uploading flags when no RunPod job is active.
  useEffect(() => {
    if (!activeBookId) return;

    const jobActive =
      lineartProgress?.isProcessing === true ||
      (activeBook?.isProcessing === true &&
        activeBook?.processingWorkflowType === 'lineart' &&
        (activeBook?.processingPercentage ?? 0) < 100);

    if (jobActive) return;

    reconcileStaleSentToRunQueue(activeBookId, { isJobActive: false });
    dispatch(clearUploadingImages());

    if (lineartProgress?.isProcessing) {
      dispatch(
        setLineartProgressByBook({
          bookId: activeBookId,
          isProcessing: false,
        }),
      );
    }

    if (
      activeBook?.isProcessing === true &&
      activeBook?.processingWorkflowType === 'lineart'
    ) {
      dispatch(setForceBookRefresh(1));
    }
  }, [
    activeBookId,
    activeBook?.isProcessing,
    activeBook?.processingWorkflowType,
    lineartProgress?.isProcessing,
    dispatch,
  ]);

  // Cleanup blob URLs on unmount ONLY
  useEffect(() => {
    return () => {
      Object.values(blobUrlCache).forEach((blobUrl) => {
        if (blobUrl && blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      });
    };
  }, [blobUrlCache]);

  //  OPTIMIZED: Check for pre-created blob URL first, then cache, then fetch
  const getBlobUrlForImage = async (image) => {
    const imageId = cropCacheKey(image);

    //  PRIORITY 1: Use pre-created blob URL from upload (INSTANT)
    if (image.blobUrl) {
      try {
        await fetch(image.blobUrl);
        console.log(' Using pre-created blob URL:', image.filename);
        return image.blobUrl;
      } catch {
        console.warn(' Pre-created blob URL died, recreating...');
      }
    }

    //  PRIORITY 2: Check cache
    const cachedBlobUrl = blobUrlCache[imageId];
    if (cachedBlobUrl) {
      try {
        await fetch(cachedBlobUrl);
        console.log(' Using cached blob URL:', image.filename);
        return cachedBlobUrl;
      } catch {
        console.warn(' Cached blob URL died, recreating...');
        setBlobUrlCache((prev) => {
          const next = { ...prev };
          delete next[imageId];
          return next;
        });
      }
    }

    //  PRIORITY 3: Create from file blob if available (FAST)
    if (image.file instanceof Blob) {
      try {
        const blobUrl = URL.createObjectURL(image.file);
        setBlobUrlCache((prev) => ({ ...prev, [imageId]: blobUrl }));
        console.log(' Created blob URL from file:', image.filename);
        return blobUrl;
      } catch (err) {
        console.error('Failed to create blob from file:', err);
      }
    }

    //  PRIORITY 4: Fetch from network (SLOW - fallback only)
    let sourceUrl =
      image.url ||
      image.originalUrl ||
      image.AIinfo?.outputUrls?.[0]?.original_presigned_url;

    if (!sourceUrl) {
      toast.error('No valid image source found');
      return null;
    }

    console.warn(' Fetching image from network (slow):', image.filename);
    try {
      const response = await fetch(sourceUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setBlobUrlCache((prev) => ({ ...prev, [imageId]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error('Failed to fetch image:', err);
      toast.error('Failed to load image for cropping');
      return null;
    }
  };

  /**
   * Save a crop cut on this device to the server. A failed save is retried
   * quietly; if every try fails the photo is marked cropUploadFailed and left
   * for Next to try again (needsCropSave), with nothing shown yet. Returns
   * whether the crop was saved.
   */
  const uploadInteriorCropPrep = useCallback(
    async (imageId, prepVersion, croppedFile, filename, mongoMediaId, { cropData = null, cropReview = null } = {}) => {
      if (!imageId || !croppedFile) return false;

      // A re-crop or a removal while waiting makes this save stale.
      const isStillCurrent = () =>
        dispatch((dispatch, getState) =>
          getState().sketch.images.some(
            (img) => getImageKey(img) === imageId && (img.prepVersion ?? 0) === prepVersion,
          ),
        );

      let uploadResult = null;
      if (!mongoMediaId && (await isStagingUploadEnabled())) {
        // Retrying can't help: the photo has no server record to attach the crop to.
        console.error('Interior crop prep skipped: photo is not linked to the server', imageId);
      } else {
        for (let attempt = 0; attempt < CROP_SAVE_TRIES && !uploadResult; attempt += 1) {
          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, CROP_SAVE_RETRY_DELAYS_MS[attempt - 1]));
            if (!isStillCurrent()) return false;
          }
          try {
            uploadResult = await uploadUserImage(
              croppedFile,
              filename || 'cropped-image.jpg',
              {
                bookId: activeBookId,
                variant: 'cropped',
                mediaId: mongoMediaId,
                cropData,
                cropReview,
              },
            );
          } catch (err) {
            console.error(`Interior crop prep upload failed (try ${attempt + 1} of ${CROP_SAVE_TRIES}):`, err);
          }
        }
      }

      if (uploadResult) {
        const croppedCloudinaryUrl = buildCropUploadRef(uploadResult);
        const croppedStagingKey = uploadResult.stagingKey || null;

        dispatch((dispatch, getState) => {
          const current = getState().sketch.images;
          let applied = false;
          const updated = current.map((img) => {
            if (getImageKey(img) !== imageId) return img;
            if ((img.prepVersion ?? 0) !== prepVersion) return img;

            applied = true;
            const croppedDisplayUrl =
              getCroppedCloudinaryDisplayUrl({
                croppedCloudinaryUrl,
              }) ||
              uploadResult.previewUrl ||
              uploadResult.url;
            return {
              ...img,
              isLoadingCover: false,
              cropUploadFailed: false,
              nextSaveFailures: 0,
              croppedCloudinaryUrl,
              croppedStagingKey,
              isCropped: true,
              ...(croppedDisplayUrl ? { url: croppedDisplayUrl } : {}),
            };
          });

          if (applied) {
            dispatch(setImages(updated));
            if (activeBookId) {
              savePendingImages(activeBookId, updated);
            }
          }
        });
        return true;
      }

      dispatch((dispatch, getState) => {
        const current = getState().sketch.images;
        let applied = false;
        const updated = current.map((img) => {
          if (getImageKey(img) !== imageId) return img;
          if ((img.prepVersion ?? 0) !== prepVersion) return img;

          applied = true;
          return {
            ...img,
            isLoadingCover: false,
            cropUploadFailed: true,
          };
        });

        if (applied) {
          dispatch(setImages(updated));
          if (activeBookId) {
            savePendingImages(activeBookId, updated);
          }
        }
      });
      return false;
    },
    [activeBookId, dispatch, stagingUploadEnabled],
  );

  /**
   * Next: save every crop that never reached the server (needsCropSave) before
   * the drawing starts. Uses the cut copy while this page still has it; after a
   * reload the crop is cut again from the full photo at its saved box. Photos
   * that still don't save get nextSaveFailures, which marks them in red.
   * Returns the keys of those photos.
   */
  const saveUnsavedCrops = async (unsaved) => {
    const keys = new Set(unsaved.map(getImageKey));
    dispatch((dispatch, getState) => {
      dispatch(
        setImages(
          getState().sketch.images.map((img) =>
            keys.has(getImageKey(img)) ? { ...img, isLoadingCover: true, cropUploadFailed: false } : img,
          ),
        ),
      );
    });

    const saveOne = async (img) => {
      try {
        let file = img.isCropped && img.file instanceof Blob ? img.file : null;
        if (!file) {
          let sourceUrl = null;
          if (img.originalFile instanceof Blob) {
            sourceUrl = URL.createObjectURL(img.originalFile);
          } else if (img.originalUrl && !img.originalUrl.startsWith('blob:')) {
            const response = await fetch(img.originalUrl);
            if (!response.ok) throw new Error(`Could not load the full photo (${response.status})`);
            sourceUrl = URL.createObjectURL(await response.blob());
          } else {
            throw new Error('No full photo to cut the crop from');
          }
          try {
            const blob = await recutCropFromData(sourceUrl, img.cropData);
            file = new File([blob], img.filename || 'cropped-image.jpg', { type: 'image/jpeg', lastModified: Date.now() });
          } finally {
            URL.revokeObjectURL(sourceUrl);
          }
        }
        return await uploadInteriorCropPrep(getImageKey(img), img.prepVersion ?? 0, file, img.filename, getMediaMongoId(img), {
          cropData: img.cropData,
          cropReview: img.cropReview,
        });
      } catch (err) {
        console.error('Could not save a crop at Next:', img.filename, err);
        return false;
      }
    };

    // Two at a time, like adding photos: cutting crops again holds full photos in memory.
    const failedKeys = new Set();
    let cursor = 0;
    const worker = async () => {
      while (cursor < unsaved.length) {
        const img = unsaved[cursor];
        cursor += 1;
        if (!(await saveOne(img))) failedKeys.add(getImageKey(img));
      }
    };
    await Promise.all(Array.from({ length: Math.min(2, unsaved.length) }, worker));

    dispatch((dispatch, getState) => {
      const updated = getState().sketch.images.map((img) => {
        const key = getImageKey(img);
        if (!keys.has(key)) return img;
        return failedKeys.has(key)
          ? { ...img, isLoadingCover: false, cropUploadFailed: true, nextSaveFailures: (img.nextSaveFailures || 0) + 1 }
          : { ...img, isLoadingCover: false };
      });
      dispatch(setImages(updated));
      savePendingImages(activeBookId, updated);
    });
    return [...failedKeys];
  };

  const handleCropSave = async ({ blob, url, cropData }) => {
    // Validate required parameters
    if (!blob || !url || !cropData) {
      console.error('Invalid crop data received:', {
        blob: !!blob,
        url: !!url,
        cropData: !!cropData,
      });
      toast.error(CROP_SAVE_FAILED_MESSAGE);
      return;
    }

    if (!croppingImage) {
      console.error('No cropping image reference found');
      return;
    }
    countBuilderAction(BUILDER_ACTIONS.CROP_SAVED);
    if (croppingImage.cropReview?.flagged) countBuilderAction(BUILDER_ACTIONS.CROP_FLAGGED_SAVED);

    const prepVersion = (croppingImage.prepVersion ?? 0) + 1;

    // Set loading state immediately so the grid shows the animation while Cloudinary upload is in progress
    const loadingImages = images.map((img) => {
      const cond = !!img._id
        ? img._id === croppingImage._id
        : img.id === croppingImage.id;
      return cond
        ? { ...img, isLoadingCover: true, prepVersion, cropUploadFailed: false }
        : img;
    });
    dispatch(setImages(loadingImages));
    savePendingImages(activeBookId, loadingImages);

    try {
      const getFilename = (img) => {
        if (img.originalFilename) return img.originalFilename;
        if (img.file?.name) return img.file.name;
        if (img.filename) return img.filename;
        return `cropped-image-${Date.now()}.jpg`;
      };

      if (!(blob instanceof Blob)) {
        throw new Error('Invalid blob object received');
      }

      // Upload the cropped blob to Cloudinary so the crop persists across refresh
      const cropFilename = getFilename(croppingImage);
      const newFile = new File([blob], cropFilename, {
        type: blob.type || 'image/jpeg',
        lastModified: Date.now(),
      });

      let croppedCloudinaryUrl = null;
      let croppedStagingKey = null;
      try {
        const mongoMediaId = getMediaMongoId(croppingImage);
        if (stagingUploadEnabled && !mongoMediaId) {
          throw Object.assign(new Error('Photo has no server record'), {
            customerMessage: "This photo didn't finish uploading. Please remove it and add it again.",
          });
        }
        const uploadResult = await uploadUserImage(newFile, cropFilename, {
          bookId: activeBookId,
          variant: 'cropped',
          mediaId: mongoMediaId,
          cropData: { ...cropData, frame: cropData.frame },
          cropReview: null,
        });
        croppedCloudinaryUrl = buildCropUploadRef(uploadResult);
        croppedStagingKey = uploadResult.stagingKey || null;
      } catch (uploadErr) {
        if (stagingUploadEnabled) {
          throw uploadErr;
        }
        console.warn('Failed to upload crop (crop will not persist on refresh):', uploadErr);
      }

      if (stagingUploadEnabled && !croppedCloudinaryUrl) {
        throw new Error('Could not save your crop to the server. Please try again.');
      }

      dispatch((dispatch, getState) => {
        const current = getState().sketch.images;
        let applied = false;
        const updated = current.map((img) => {
          const cond = !!img._id
            ? img._id === croppingImage._id
            : img.id === croppingImage.id;

          if (!cond) return img;
          if ((img.prepVersion ?? 0) !== prepVersion) return img;

          applied = true;
          const filename = getFilename(img);

          const fileObj = new File([blob], filename, {
            type: blob.type || 'image/jpeg',
            lastModified: Date.now(),
          });

          const croppedDisplayUrl = getCroppedCloudinaryDisplayUrl({
            croppedCloudinaryUrl,
          });

          return {
            ...img,
            url: croppedDisplayUrl || url,
            file: fileObj,
            originalFile: img.originalFile || img.file,
            meta: null,
            AIUrl: null,
            AIinfo: null,
            cropData,
            // A hand-made crop needs no second look.
            cropReview: null,
            originalUrl: img.originalUrl || img.url,
            originalFilename:
              img.originalFilename || img.filename || filename,
            lastModified: Date.now(),
            isCropped: true,
            isLoadingCover: false,
            cropUploadFailed: !croppedCloudinaryUrl,
            nextSaveFailures: croppedCloudinaryUrl ? 0 : img.nextSaveFailures,
            prepVersion,
            blobUrl: url,
            croppedCloudinaryUrl,
            croppedStagingKey,
          };
        });

        if (!applied) return;

        dispatch(setImages(updated));
        if (croppedCloudinaryUrl || !stagingUploadEnabled) {
          const cropKey = getImageKey(croppingImage);
          if (cropKey) clearSentToRunForImage(activeBookId, cropKey);
          if (croppingImage._id && String(croppingImage._id) !== String(cropKey)) {
            clearSentToRunForImage(activeBookId, croppingImage._id);
          }
        }
        savePendingImages(activeBookId, updated);
      });
      setCroppingImage(null);
      notifySaved();
      return true;
    } catch (error) {
      console.error('Error in handleCropSave:', error);
      // The technical reason stays in the console; the customer gets plain words.
      toast.error(error.customerMessage || CROP_SAVE_FAILED_MESSAGE);
      dispatch((dispatch, getState) => {
        const current = getState().sketch.images;
        const updated = current.map((img) => {
          const cond = !!img._id
            ? img._id === croppingImage._id
            : img.id === croppingImage.id;
          return cond ? { ...img, isLoadingCover: false } : img;
        });
        dispatch(setImages(updated));
      });
      return false;
    }
  };

  const handleImageUpload = async (files) => {
    if (blockIfLineartLocked()) return;
    countBuilderAction(BUILDER_ACTIONS.PHOTOS_ADDED, files?.length);

    const newImages = files.map((file) => {
      const mongoId =
        file.mediaId && isMongoMediaId(String(file.mediaId))
          ? String(file.mediaId)
          : null;
      const fileBlob =
        file.file instanceof Blob
          ? file.file
          : file.file?.data instanceof Blob
            ? file.file.data
            : null;
      let blobUrl = file.blobUrl || null;
      if (!blobUrl && fileBlob) {
        blobUrl = URL.createObjectURL(fileBlob);
      }

      return {
        id: mongoId || uuidv4(),
        ...(mongoId ? { _id: mongoId } : {}),
        url: file.url,
        publicId: file.publicId,
        stagingKey: file.stagingKey || file.publicId || null,
        file: fileBlob,
        originalFile: fileBlob,
        blobUrl,
        filename: file.filename,
        originalUrl: file.url,
        cropData: null,
        prepVersion: 0,
      };
    });

    let noDetectorCount = 0;
    const autoCroppedImages = await Promise.all(
      newImages.map(async (img) => {
        let tempSourceUrl = null;

        try {
          let sourceUrl = img.blobUrl;
          if (!sourceUrl && img.file instanceof Blob) {
            tempSourceUrl = URL.createObjectURL(img.file);
            sourceUrl = tempSourceUrl;
          }

          if (!sourceUrl) return img;

          const cropStartedAt = Date.now();
          const { blob: croppedBlob, cropData: autoCropData, method: cropMethod, faceCount, review: cropReview } =
            await autoCropImageWithRect(sourceUrl);
          console.log(
            `Auto-crop (${cropMethod}, ${faceCount} faces) took ${Date.now() - cropStartedAt}ms for`,
            img.filename,
            '| review',
            JSON.stringify(cropReview),
          );
          if (cropMethod === 'center-no-detector') noDetectorCount += 1;
          const croppedBlobUrl = URL.createObjectURL(croppedBlob);
          const croppedFile = new File(
            [croppedBlob],
            img.filename || 'cropped-image.jpg',
            {
              type: 'image/jpeg',
              lastModified: Date.now(),
            },
          );

          return {
            ...img,
            url: croppedBlobUrl,
            file: croppedFile,
            blobUrl: croppedBlobUrl,
            originalUrl: img.originalUrl || img.url,
            originalFile: img.originalFile || img.file,
            // Source-pixel rectangle of the auto crop, so the crop tool opens
            // with its box already where the auto crop put it.
            cropData: autoCropData,
            // Whether the auto crop is worth a look, and why (review rules).
            cropReview,
            isCropped: true,
            isLoadingCover: true,
            cropUploadFailed: false,
            prepVersion: 1,
          };
        } catch (err) {
          console.warn('Auto-crop failed for', img.filename, '- using original:', err);
          // No crop means no check either, so ask the customer to look (decided 2026-11-02).
          // The server centres the crop when the drawing starts.
          return { ...img, cropReview: { flagged: true, reasons: ['crop-failed'] } };
        } finally {
          if (tempSourceUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(tempSourceUrl);
          }
        }
      }),
    );

    const flaggedCount = autoCroppedImages.filter((img) => img.cropReview?.flagged).length;
    if (flaggedCount > 0) countBuilderAction(BUILDER_ACTIONS.CROP_FLAGGED, flaggedCount);
    if (noDetectorCount > 0) countBuilderAction(BUILDER_ACTIONS.CROP_NO_DETECTOR, noDetectorCount);

    let imagesToPrep = [];

    dispatch((dispatch, getState) => {
      const currentImages = getState().sketch.images;
      const { merged, added, replaced, duplicates: duplicatesCount } =
        reconcileIncomingUploads(currentImages, autoCroppedImages);
      const filtered = [...replaced, ...added];

      if (duplicatesCount > 0) {
        toast.warning(
          `${duplicatesCount} duplicate image${
            duplicatesCount > 1 ? 's' : ''
          } filtered out`,
        );
      }

      if (filtered.length === 0) return;

      imagesToPrep = filtered;
      console.log(' Adding auto-cropped images:', filtered);
      dispatch(setImages(merged));
      savePendingImages(activeBookId, merged);

      notifySaved();
    });

    // The server numbers new pages in the order their uploads finished, not the
    // order they were picked. Save the grid's order now, or a reload (or Back,
    // then Next) before "Next: Cover and note" reshuffles the batch.
    // A replace saves its own order below.
    if (imagesToPrep.length && !replaceTargetRef.current) {
      dispatch((dispatch, getState) => {
        persistPageOrder(getState().sketch.images, { refreshAfter: false }).catch((err) =>
          console.error('Could not save the page order after adding photos:', err),
        );
      });
    }

    // "Replace photo" from the crop screen: the new photo takes the old one's
    // place in the page order, then the old one goes.
    const replaceKey = replaceTargetRef.current;
    if (replaceKey && imagesToPrep.length) {
      replaceTargetRef.current = null;
      const newKeys = new Set(imagesToPrep.map(getImageKey));
      dispatch((dispatch, getState) => {
        const current = getState().sketch.images;
        const oldIndex = current.findIndex((img) => getImageKey(img) === replaceKey);
        const rest = current.filter((img) => !newKeys.has(getImageKey(img)));
        const fresh = current.filter((img) => newKeys.has(getImageKey(img)));
        const at = oldIndex === -1 ? rest.length : oldIndex + 1;
        const reordered = [...rest.slice(0, at), ...fresh, ...rest.slice(at)];
        dispatch(setImages(reordered));
        savePendingImages(activeBookId, reordered);
      });
      await handleDeleteImage(replaceKey);
      dispatch((dispatch, getState) => {
        persistPageOrder(getState().sketch.images, { refreshAfter: false }).catch((err) =>
          console.error('Could not save the page order after replacing a photo:', err),
        );
      });
    }

    const prepTargets = imagesToPrep.filter(
      (img) => img.isCropped && img.file instanceof Blob && getImageKey(img),
    );

    const runCropPrep = (img) =>
      uploadInteriorCropPrep(
        getImageKey(img),
        img.prepVersion ?? 1,
        img.file,
        img.filename,
        getMediaMongoId(img),
        { cropData: img.cropData, cropReview: img.cropReview },
      );

    if (await isStagingUploadEnabled()) {
      // Staging path finalizes on the API's 2-vCPU box; bound the burst so
      // init/PUT/finalize don't all fire at once. (Cloudinary users unchanged.)
      const CROP_PREP_CONCURRENCY = 2;
      let cursor = 0;
      const worker = async () => {
        while (cursor < prepTargets.length) {
          const img = prepTargets[cursor];
          cursor += 1;
          try {
            await runCropPrep(img);
          } catch (err) {
            console.error('Interior crop prep upload failed:', err);
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(CROP_PREP_CONCURRENCY, prepTargets.length) },
          worker,
        ),
      );
    } else {
      for (const img of prepTargets) {
        void runCropPrep(img);
      }
    }
  };



  /** Funnel: the customer has moved past the photo step with photos in place. */
  const trackPhotosUploaded = () => {
    const photoCount = (images || []).filter(
      (img) => !img?.isCover && !img?.deletedAt,
    ).length;
    if (photoCount === 0) return;
    const activeBook = profile?.myBooks?.find((book) => book._id === activeBookId);
    pushFunnelEvent(FUNNEL_EVENTS.PHOTOS_UPLOADED, {
      bookId: activeBookId,
      profile: profile?.data,
      tag: resolveBookTag(activeBook, tag),
      extras: { photo_count: photoCount },
    });
  };

  const navigateToCoverStep = () => {
    trackPhotosUploaded();
    dispatch(
      markStepAsVisited({
        id: activeBookId,
        currentStepCode: 'Design_Your_Cover_Page',
      }),
    );
    navigate(`/cover-image?stepCode=${'Design_Your_Cover_Page'}${tag ? `&tag=${tag}` : ""}`);
    saveStep({
      userId: session?.user?._id,
      bookId: activeBookId,
      isCompleted: 'yes',
      stepCode: 'Design_Your_Cover_Page',
    }).catch((err) => console.error('Error saving step in background:', err));
  };

  const persistPageOrder = async (orderedImages, { refreshAfter = true } = {}) => {
    await persistInteriorPageOrder(orderedImages);
    if (refreshAfter) {
      dispatch(setForceBookRefresh(1));
    }
  };

  const blockIfLineartLocked = () => {
    if (!isLineartLocked) return false;
    toast.info(
      'Your pages are still being illustrated. Please wait until generation finishes before changing photos.',
    );
    return true;
  };

  // Where "Continue with N" goes once the fewer-than-20 pop-up is answered:
  // the cover step from Next, or the step a progress-bar tap asked for.
  const goOnAfterFewPhotosRef = useRef(null);

  /**
   * Next, and forward taps on the header's progress bar: the same checks and
   * prompts, then line art starts in the background and `goOn` moves on at once.
   */
  const handleSketchGeneration = async (goOn = navigateToCoverStep) => {
    if (generationInFlightRef.current) return;

    if (images.length > 20) {
      setModalImageCount(images.length);
      setShowImageLimitModal(true);
      countBuilderAction(BUILDER_ACTIONS.TOO_MANY_PHOTOS_PROMPT_SHOWN);
      return;
    }

    if (isLineartLocked) {
      // Generation is already in flight for this book, so whatever needed line
      // art has already been sent to RunPod. Starting a second run could
      // duplicate pages, so we never resend here — and we never trap the user
      // behind a toast. Let them continue to the next step while generation
      // finishes in the background. (Before the fewer-than-20 prompt: these
      // photos are already being drawn, so there is nothing left to decide.)
      goOn();
      return;
    }

    if (images.length < 20 && images.length > 0) {
      goOnAfterFewPhotosRef.current = goOn;
      setModalImageCount(images.length);
      setShowLessThan20Modal(true);
      countBuilderAction(BUILDER_ACTIONS.FEW_PHOTOS_PROMPT_SHOWN);
      return;
    }

    await proceedWithGeneration(goOn);
  };

  const proceedWithGeneration = async (goOn = navigateToCoverStep) => {
    if (generationInFlightRef.current) return;

    try {
      if (!activeBookId) {
        if (
          profile?.data?.latestStepCode?.code === 'Upload_Images' &&
          !!profile?.data?.isOrderPlaces
        ) {
          setIsOpenBookSelectionModal(true);
        }
        return;
      }

      if (!coverImages?.length) {
        navigate(`/dashboard?stepCode=Upload_Your_Cover_Photo${tag ? `&tag=${tag}` : ""}`);
        toast.error('Please choose a cover image first.');
        return;
      }

      if (!images?.length) {
        toast.error('Please choose some images first.');
        return;
      }

      if (isChoosePhotosNextBlockedByCropPrep(images, activeBookId, uploadingImages)) {
        toast.info('Please wait — your photos are still being prepared for illustration.');
        return;
      }

      // Photos removed moments ago are still waiting on their Undo bar: delete
      // them now, or /run counts them and refuses a book over 20.
      flushPendingRemovals();
      dismissMessageBar();
      generationInFlightRef.current = true;
      setIsStartingGeneration(true);
      failedPhotoDeletesRef.current.forEach(sendPhotoDelete);
      await Promise.all(pendingPhotoDeletesRef.current);
      generationInFlightRef.current = false;
      setIsStartingGeneration(false);
      if (failedPhotoDeletesRef.current.size > 0) {
        goOnAfterDeleteFailedRef.current = goOn;
        setShowDeleteFailed(true);
        return;
      }

      // Crops that never reached the server are saved now. Left to the server, it
      // would draw a centre crop instead of the crop the customer saw and checked.
      const unsaved = images.filter(needsCropSave);
      if (unsaved.length) {
        generationInFlightRef.current = true;
        setIsStartingGeneration(true);
        const failed = await saveUnsavedCrops(unsaved);
        generationInFlightRef.current = false;
        setIsStartingGeneration(false);
        // The red note and marks explain it; the customer stays here.
        if (failed.length) return;
      }
      const currentImages = dispatch((dispatch, getState) => getState().sketch.images);

      await persistPageOrder(currentImages, { refreshAfter: false });
      savePendingImages(activeBookId, currentImages);

      const missingLineart = getImagesMissingLineart(currentImages);

      if (missingLineart.length === 0 && allImagesGenerated(currentImages)) {
        goOn();
        return;
      }

      let uploadableImages = getUploadableImages(
        currentImages,
        activeBookId,
        uploadingImages,
      );

      if (uploadableImages.length === 0 && missingLineart.length > 0) {
        missingLineart.forEach((img) => {
          const key = getImageKey(img);
          if (key) clearSentToRunForImage(activeBookId, key);
          if (img._id && String(img._id) !== String(key)) {
            clearSentToRunForImage(activeBookId, img._id);
          }
        });
        uploadableImages = getUploadableImages(
          currentImages,
          activeBookId,
          uploadingImages,
        );
      }

      const useServerRunPayload = await isStagingUploadEnabled();
      const shouldStartLineartJob =
        uploadableImages.length > 0 ||
        (useServerRunPayload && missingLineart.length > 0);

      if (!shouldStartLineartJob) {
        goOn();
        return;
      }

      if (isLineartLocked) {
        // Generation is already in flight. Don't start a second run (it could
        // duplicate pages) and don't trap the user — let them continue while
        // the in-flight generation finishes.
        goOn();
        return;
      }

      generationInFlightRef.current = true;
      setIsStartingGeneration(true);
      console.log(' Starting lineart generation process...');

      const useServerRunPayloadForQueue = await isStagingUploadEnabled();
      const imagesForRun = tagUploadableWithGridOrder(
        uploadableImages.length > 0 ? uploadableImages : missingLineart,
        currentImages,
      );
      const queueImageKeys = (
        uploadableImages.length > 0 ? uploadableImages : missingLineart
      )
        .map((img) => getImageKey(img))
        .filter(Boolean);

      if (!useServerRunPayloadForQueue) {
        dispatch(setUploadingImages(queueImageKeys));
      }

      goOn();

      void processAndStartLineartJob(
        imagesForRun,
        activeBookId,
        session?.user?._id,
      )
        .then((started) => {
          if (started && useServerRunPayloadForQueue) {
            dispatch(setUploadingImages(queueImageKeys));
          }
        })
        .finally(() => {
          generationInFlightRef.current = false;
          setIsStartingGeneration(false);
        });
    } catch (err) {
      console.error('Error in proceedWithGeneration:', err);
      toast.error(err.message || 'Failed to proceed. Please try again.');
      generationInFlightRef.current = false;
      setIsStartingGeneration(false);
    }
  };

  const processAndStartLineartJob = async (
    uploadableImages,
    bookId,
    userId,
  ) => {
    // Lock Choose Photos now, not when /run answers: the answer can take many
    // seconds, and a Next pressed in between would ask for a second run.
    const wasProcessing = lineartProgress?.isProcessing === true;
    dispatch(setLineartProgressByBook({ bookId, isProcessing: true, currentStage: LINEART_STAGE_STARTING }));
    try {
      const useServerRunPayload = await isStagingUploadEnabled();
      let response;
      let processedImages = [];

      if (useServerRunPayload) {
        console.log(
          ' Starting server-driven lineart /run from DB staging keys...',
        );
        response = await run({ bookId });
      } else {
        console.log(' Processing images in background...');

        processedImages = await processAllImagesForRun(
          uploadableImages,
          activeBookId,
        );

        if (!processedImages || processedImages.length === 0) {
          throw new Error('Failed to process images');
        }

        console.log(
          ' All images processed and ready for /run:',
          processedImages,
        );

        response = await run({
          imageUrls: processedImages,
          bookId: bookId,
        });
      }

      // 409: the server is already drawing this book's pages (this tab lost
      // track of the run). Nothing to start and nothing to tell the customer.
      if (response?.httpStatus === 409) {
        console.log(' Line art already in progress on the server — keeping the photos locked');
        dispatch(setLineartProgressByBook({ bookId, currentStage: 'ai_processing' }));
        return true;
      }

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to start job');
      }

      const queuedMediaIds = new Set(
        (response?.data?.mediaIds || []).map((id) => String(id)),
      );

      uploadableImages.forEach((img, index) => {
        if (useServerRunPayload && queuedMediaIds.size > 0) {
          const mongoId = getMediaMongoId(img);
          if (!mongoId || !queuedMediaIds.has(String(mongoId))) {
            return;
          }
        }

        const processed = processedImages[index];
        const payload = useServerRunPayload
          ? {
              publicId:
                img.croppedStagingKey ||
                img.stagingKey ||
                img.publicId ||
                getMediaMongoId(img),
              url: img.originalUrl || img.url,
            }
          : {
              publicId: processed?.mediaId,
              url: processed?.url,
            };
        const key = getImageKey(img);
        if (key) markImageAsSentToRun(bookId, key, payload);
        if (img._id && String(img._id) !== String(key)) {
          markImageAsSentToRun(bookId, img._id, payload);
        }
      });

      // Images are now being processed — do NOT clear yet; wait for API/Socket to confirm full count
      // clearPendingImages(bookId);

      const totalSent =
        Number(response?.data?.totalImages) ||
        uploadableImages.length;

      // Grid anchor = FULL interior page count (every page needs a slot on
      // Preview), NOT just this run's batch. A partial re-run sends only the
      // changed/new images, so anchoring on the batch would collapse the grid.
      // Prefer the server's authoritative count; fall back to the client grid.
      const clientInteriorCount = (images || []).filter(
        (img) => !img?.isCover && !img?.deletedAt,
      ).length;
      const expectedInteriorCount =
        Number(response?.data?.expectedInteriorCount) ||
        clientInteriorCount ||
        totalSent;

      // Authoritative reset for this run (clears any prior run's high-water mark).
      setExpectedInteriorPageCount(bookId, expectedInteriorCount);

      dispatch(setLineartProgressByBook({
        ...initialLineartProgress,
        isProcessing: true,
        currentStage: 'ai_processing',
        totalImages: expectedInteriorCount,
        bookId
      }));

      console.log(' Lineart job started:', response.data);
      return true;
    } catch (err) {
      console.error('Error in background lineart processing:', err);
      toast.error(
        err.message || 'Failed to start image generation. Please try again.',
      );
      dispatch(setUploadingImages([]));
      dispatch(
        setLineartProgressByBook(
          wasProcessing
            ? { bookId, currentStage: 'ai_processing' }
            : { bookId, isProcessing: false, currentStage: null },
        ),
      );
      return false;
    }
  };

  const handleUploadMore = () => {
    countBuilderAction(BUILDER_ACTIONS.FEW_PHOTOS_ADDED_MORE);
    goOnAfterFewPhotosRef.current = null;
    setShowLessThan20Modal(false);
    uploaderRef.current?.openPicker();
  };

  const handleContinue = async () => {
    countBuilderAction(BUILDER_ACTIONS.FEW_PHOTOS_CONTINUED);
    const goOn = goOnAfterFewPhotosRef.current || navigateToCoverStep;
    goOnAfterFewPhotosRef.current = null;
    setShowLessThan20Modal(false);
    await proceedWithGeneration(goOn);
  };

  const handleDeleteImage = async (id) => {
    if (blockIfLineartLocked()) return;
    const toDelete = images.find((img) => img.id === id || img._id === id);
    if (toDelete) deleteImageRecord(toDelete);
  };

  const sendPhotoDelete = (id) => {
    const mongoId = String(id);
    removedPhotoIdsRef.current.add(mongoId);
    const pendingDelete = removeBookImage(mongoId)
      .catch(() => null)
      .then((res) => {
        // 404: an earlier try got through and only its answer was lost.
        if (res?.status === 200 || res?.status === 404) {
          failedPhotoDeletesRef.current.delete(mongoId);
        } else {
          failedPhotoDeletesRef.current.add(mongoId);
        }
        pendingPhotoDeletesRef.current.delete(pendingDelete);
      });
    pendingPhotoDeletesRef.current.add(pendingDelete);
  };

  /** Forget a photo everywhere: blob URLs, redux, pending storage, the server. */
  const deleteImageRecord = (toDelete) => {
    const id = toDelete.id ?? toDelete._id;
    {
      countBuilderAction(BUILDER_ACTIONS.PHOTOS_REMOVED);
      //  Clean up blob URLs
      const imageId = cropCacheKey(toDelete);

      // Revoke cached blob URL
      if (blobUrlCache[imageId] && blobUrlCache[imageId].startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlCache[imageId]);
        setBlobUrlCache((prev) => {
          const updated = { ...prev };
          delete updated[imageId];
          return updated;
        });
      }

      // Revoke pre-created blob URL
      if (toDelete.blobUrl && toDelete.blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(toDelete.blobUrl);
      }

      setDeletedFilenames((prev) => [...prev, toDelete.filename]);
      dispatch(deleteImage(id));
      const pendingKey = getImageKey(toDelete);
      if (pendingKey) removePendingImage(activeBookId, pendingKey);
      if (toDelete.id && toDelete._id && String(toDelete.id) !== String(toDelete._id)) {
        removePendingImage(activeBookId, toDelete._id);
      }
      const mongoId = getMediaMongoId(toDelete);
      if (mongoId) {
        sendPhotoDelete(mongoId);
      }
    }
  };

  const onDragEnd = async (reorderedImages) => {
    if (blockIfLineartLocked()) return;
    countBuilderAction(BUILDER_ACTIONS.REORDERED);

    savePendingImages(activeBookId, reorderedImages);
    await persistInteriorPageOrder(reorderedImages);
    // Staging books: the grid already shows the new order and the PATCH above
    // persisted it. Skip the forced refetch — it re-signs every image URL
    // (fresh signature per request), so every <img> src changes and the
    // browser visibly re-downloads all thumbnails. Legacy path unchanged.
    if (await isStagingUploadEnabled()) return;
    dispatch(setForceBookRefresh(1));
  };

  const cropPrepBlocking =
    !isLineartLocked &&
    isChoosePhotosNextBlockedByCropPrep(
      images,
      activeBookId,
      uploadingImages,
    );
  const nextButtonDisabled =
    images.length === 0 || isStartingGeneration || cropPrepBlocking;

  // Handle opening crop modal with blob URL
  // Uses the ORIGINAL uncropped image so users can re-crop freely
  const handleEditImage = async (image) => {
    if (blockIfLineartLocked()) return;
    countBuilderAction(BUILDER_ACTIONS.CROP_OPENED);
    if (image?.cropReview?.flagged) countBuilderAction(BUILDER_ACTIONS.CROP_FLAGGED_OPENED);
    // Show loading state while fetching blob
    // const loadingToast = toast.info('Loading image...', { autoClose: false });

    let blobUrl = null;

    // Priority 1: Use originalFile (full uncropped image) if available
    if (image.originalFile instanceof Blob) {
      try {
        blobUrl = URL.createObjectURL(image.originalFile);
      } catch (err) {
        console.warn('Failed to create blob URL from originalFile:', err);
      }
    }

    // Priority 2: Fetch from originalUrl (Cloudinary URL of full image)
    if (!blobUrl && image.originalUrl && !image.originalUrl.startsWith('blob:')) {
      try {
        const response = await fetch(image.originalUrl);
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);
      } catch (err) {
        console.warn('Failed to fetch originalUrl:', err);
      }
    }

    // Priority 3: Fall back to regular getBlobUrlForImage (cropped version)
    if (!blobUrl) {
      blobUrl = await getBlobUrlForImage(image);
    }

    // if (loadingToast) {
    //   toast.dismiss(loadingToast);
    // }

    if (blobUrl) {
      setCroppingImage({
        ...image,
        blobUrl, // Pass blob URL for cropper (now uses original uncropped image)
      });
    }
  };

  // ---- step 3 on the redesign (docs/design/HANDOFF.md: Photos, PhotosLocked, PhotosCrop, PhotosCover)

  const count = images.length;
  const flaggedCount = images.filter((img) => img.cropReview?.flagged).length;
  // A tile's "drawn" badge. The grid is refreshed from the server's pages while locked (above).
  const isDrawn = (img) => isImageGenerated(img) && !img.isGenerating;
  const isPreparing = (img) => !!img.isLoadingCover || isPhotoCropPrepInProgress(img) || !!img.regenerationProcessing;
  // Photos whose crop Next couldn't save (marked in red); after two failed Nexts the note asks for another photo.
  const isUnsaved = (img) => (img.nextSaveFailures || 0) > 0 && !isPreparing(img);
  const unsavedPhotos = images.filter(isUnsaved);
  const unsavedAgain = unsavedPhotos.some((img) => img.nextSaveFailures >= 2);
  // Photos still uploading ("Adding") or being prepared (auto crop, a crop being saved): no crop, no Next,
  // no leaving the step until they're stored (leaving would cancel the uploads).
  const addingPhotos = uploaderBusy > 0 || placingPicked;
  const photosBusy = !isLineartLocked && (addingPhotos || cropPrepBlocking || images.some(isPreparing));
  useBuilderBusy(photosBusy);

  const cover = coverImages[0];
  const coverReady = !!(cover?.AIUrl || cover?.AIinfo?.aws_upload?.s3_url);
  // The image API's 1200px preview (same width as the API's AIUrlPreview), not the full-size PNG.
  const coverAIUrl = cover?.AIUrl && cover.AIUrl.includes('?id=') && !cover.AIUrl.includes('&w=')
    ? `${cover.AIUrl}&w=1200`
    : cover?.AIUrl;
  const coverDisplayUrl = coverAIUrl || cover?.AIinfo?.aws_upload?.s3_url || cover?.url || null;
  const coverMaking =
    !!cover && !coverReady && (uploadingCoverImage || cover.status === 'PROCESSING' || !!loadPendingCoverImage(activeBookId)?.sentToRun);
  // A failed drawing (API drawStatus) wins over the drawing flags: they only clear on a socket event.
  const { failed: coverFailed, retry: retryCover, retrying: coverRetrying, usedUp: coverUsedUp } = useCoverRetry(activeBookId, cover);
  const coverState = !cover ? null : coverReady ? 'ready' : coverFailed ? 'failed' : coverMaking ? 'making' : null;

  const showPrice = !isEtsyUser(profile?.data) && !activeBook?.gift?.giftOrderId;

  const openPicker = () => {
    if (blockIfLineartLocked()) return;
    // A cancelled "Replace photo" must not turn the next upload into a replacement.
    replaceTargetRef.current = null;
    if (!uploaderRef.current?.isReady?.()) {
      toast.info('One moment, the uploader is still getting ready.');
      return;
    }
    if (shouldShowPickerHeadsUp()) {
      setPickerHeadsUpOpen(true);
      return;
    }
    uploaderRef.current.openPicker();
  };
  // The heads-up has its own file input (the picker opens from its button); photos picked there go to the uploader.
  const addFromHeadsUp = (files) => {
    setPickerHeadsUpOpen(false);
    uploaderRef.current?.addFiles(files);
  };

  const goBackToCover = () => {
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: 'yes', stepCode: 'Upload_Your_Cover_Photo' });
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Upload_Your_Cover_Photo' }));
    navigate(`/dashboard?stepCode=Upload_Your_Cover_Photo${tag ? `&tag=${tag}` : ''}`);
  };
  useBuilderBack(goBackToCover);

  // A forward tap on the header's progress bar is Next, landing on the tapped
  // step: same checks and prompts, line art starts in the background, no wait.
  useBuilderStepInterceptor(({ stepNumber: target, navigate: performNavigate }) => {
    if (target <= 3) {
      performNavigate();
      return;
    }
    void handleSketchGeneration(() => {
      trackPhotosUploaded();
      performNavigate();
    });
  });

  // ---- crop screen: one photo at a time; a saved crop closes it
  const keyOf = (img) => getImageKey(img);
  // The grid as it is right now, for Undo (several removals can be undone at once).
  const imagesRef = useRef(images);
  imagesRef.current = images;

  // Keep the locked grid in step with the server even when this phone misses
  // the socket events: show newly drawn pages as the count rises, and finish
  // the run (unlock) once every page is drawn. The socket's completed handler
  // does the same finish when it hears the event first.
  const shownDoneRef = useRef(null);
  useEffect(() => {
    if (!activeBookId || !serverPagesFresh || !Array.isArray(serverPages)) return;
    const status = lineartStatusFromRows(serverPages, activeBookId, { activeBook, lineartProgress });
    const runActiveHere =
      lineartProgress?.isProcessing === true ||
      (activeBook?.isProcessing === true && activeBook?.processingWorkflowType === 'lineart') ||
      hasImagesSentToRunPending(activeBookId);
    if (!isLineartLocked && !runActiveHere) {
      shownDoneRef.current = null;
      return;
    }
    const finishNow = status.finished && runActiveHere && !generationInFlightRef.current;
    if (!finishNow && shownDoneRef.current === status.done) return;
    shownDoneRef.current = status.done;

    let cancelled = false;
    fetchOrderedInteriorImages(activeBookId, { rows: serverPages })
      .then((merged) => {
        if (cancelled) return;
        const next = withoutRemovedPhotos(preserveInMemoryGridUploads(merged, imagesRef.current, sortByPageOrdering));
        if (finishNow) {
          console.log(' Server shows every page drawn — finishing the run');
          markLineartRunFinished(dispatch, activeBookId, next);
        } else {
          dispatch(setImages(next));
        }
      })
      .catch((error) => console.error('Failed to refresh pages from the server:', error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the server's pages change
  }, [serverPages, serverPagesFresh, activeBookId, isLineartLocked]);
  /**
   * The X on a tile: the photo leaves the grid at once and the message bar
   * offers Undo. Removals in a row share one bar ("3 photos removed"); its Undo
   * puts each photo back where it was. The photos are deleted when the bar closes.
   */
  const removeWithUndo = (image) => {
    if (blockIfLineartLocked()) return;
    const key = keyOf(image);
    const index = images.findIndex((img) => keyOf(img) === key);
    const remaining = images.filter((img) => keyOf(img) !== key);
    dispatch(setImages(remaining));
    imagesRef.current = remaining;
    const commit = () => deleteImageRecord(image);
    pendingRemovalsRef.current.set(key, commit);
    const mongoId = getMediaMongoId(image);
    if (mongoId) removedPhotoIdsRef.current.add(String(mongoId));
    showMessageBar({
      text: 'Photo removed',
      mergeKey: 'page-photo-removed',
      mergedText: (n) => `${n} photos removed`,
      onUndo: () => {
        pendingRemovalsRef.current.delete(key);
        if (mongoId) removedPhotoIdsRef.current.delete(String(mongoId));
        const grid = imagesRef.current;
        if (grid.some((img) => keyOf(img) === key)) return;
        const restored = [...grid];
        restored.splice(Math.min(Math.max(index, 0), restored.length), 0, image);
        dispatch(setImages(restored));
        imagesRef.current = restored;
      },
      onClose: ({ undone }) => {
        if (undone) return;
        if (pendingRemovalsRef.current.delete(key)) commit();
      },
    });
  };

  const openTile = (image) => handleEditImage(image);

  // "Replace photo" on a page that failed to draw (Preview) lands here with ?crop=<media id>:
  // open that page's crop screen once its photo is in the grid, then drop the parameter.
  const cropParam = searchParams.get('crop');
  const openedCropRef = useRef(null);
  useEffect(() => {
    if (!cropParam || openedCropRef.current === cropParam) return;
    const target = images.find((img) => [img._id, img.id, getMediaMongoId(img)].some((id) => id && String(id) === cropParam));
    if (!target) return;
    openedCropRef.current = cropParam;
    handleEditImage(target);
    const next = new URLSearchParams(searchParams);
    next.delete('crop');
    navigate({ search: `?${next.toString()}` }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropParam, images]);
  const closeCrop = () => setCroppingImage(null);
  const replaceFromCrop = () => {
    if (!croppingImage) return;
    replaceTargetRef.current = keyOf(croppingImage);
    closeCrop();
    uploaderRef.current?.openPicker();
  };
  const cropPageNumber = croppingImage ? images.findIndex((img) => keyOf(img) === keyOf(croppingImage)) + 1 : 0;

  // ---- the main action (bottom bar on mobile and tablet, side panel on desktop).
  // Under 20 photos, Next opens the "Add N more photos?" pop-up (PopFewer).
  const nextLabel = 'Next: Cover and note';
  let primary;
  let note = null;
  let noteClassName;
  // Uploading and preparing are one wait to the customer (decided 2026-11-02): the button and the status card
  // say the same words the whole time.
  const busyLabel = 'Getting photos ready';
  if (isLineartLocked) {
    primary = (
      <Button size="lg" fullWidth onClick={navigateToCoverStep}>
        {nextLabel}
      </Button>
    );
    note = "We'll keep drawing while you work on the cover.";
  } else if (count > 0) {
    primary = (
      <Button size="lg" fullWidth onClick={() => handleSketchGeneration()} busy={isStartingGeneration || photosBusy} disabled={nextButtonDisabled || photosBusy}>
        {photosBusy ? busyLabel : isStartingGeneration ? 'Starting' : nextLabel}
      </Button>
    );
  } else if (addingPhotos) {
    // The first photos are still uploading: nothing is in the grid yet.
    primary = (
      <Button size="lg" fullWidth busy>
        {busyLabel}
      </Button>
    );
  } else {
    primary = (
      <Button size="lg" fullWidth onClick={openPicker} icon={<Plus className="h-5 w-5" aria-hidden="true" />}>
        Add 20 photos
      </Button>
    );
    note = 'Pick several at once from your camera roll.';
    // Not on phones (decided 2026-10-23); tablet and desktop keep it.
    noteClassName = 'hidden md:block';
  }
  const dropOnGrid = (files) => {
    if (blockIfLineartLocked()) return;
    replaceTargetRef.current = null;
    uploaderRef.current?.addFiles(files);
  };

  const sidePanel = (
    <BookSidePanel
      cover={
        <CoverPreview
          title={activeBook?.title}
          imageUrl={coverState === 'failed' ? null : coverDisplayUrl}
          placeholder={
            coverState === 'failed' ? (
              <CoverDrawFailedBox onRetry={retryCover} onChangePhoto={goBackToCover} retrying={coverRetrying} usedUp={coverUsedUp} />
            ) : null
          }
        />
      }
      title={activeBook?.title}
      // The caption names the book being made (Desk3Photos: "Cover + 20 pages" while photos are still being added).
      rows={[{ label: `Cover + ${Math.max(count, 20)} pages`, value: showPrice ? `$${BASE_BOOK_PRICE.toFixed(2)}` : 'Paid' }]}
      primary={primary}
      note={note}
      back={{ onClick: goBackToCover, disabled: photosBusy }}
    />
  );

  // Phones only (decided 2026-10-29): "+ Add N more photos" above Next, since the grid's Add photos tile is
  // screens away once photos are in. Gone at 20, and not on PhotosEmpty (its main button adds) or PhotosLocked.
  const missing = 20 - count;
  const addMore =
    count > 0 && missing > 0 && !isLineartLocked ? (
      <Button
        variant="secondary"
        fullWidth
        onClick={openPicker}
        icon={<Plus className="h-5 w-5" aria-hidden="true" />}
        className="!border-[#E5C5CB] !text-[17px] !text-md-pink-dark md:hidden"
      >
        Add {missing} more {missing === 1 ? 'photo' : 'photos'}
      </Button>
    ) : null;

  const bottomBar = (
    <BottomBar
      primary={primary}
      back={{ onClick: goBackToCover, disabled: photosBusy }}
      note={note}
      noteClassName={noteClassName}
      className={addMore ? '!gap-2.5 md:!gap-3' : undefined}
    >
      {addMore}
    </BottomBar>
  );

  if (isInitialLoading) {
    return (
      <WizardShell aside={sidePanel} bottomBar={bottomBar}>
        <SkeletonGroup label="Loading your photos" className="flex flex-col gap-4">
          <Skeleton shape="block" className="h-16" />
          <Skeleton shape="line" className="h-8 w-2/3" />
          <div className="grid grid-cols-2 gap-x-3 gap-y-3.5 md:gap-x-4 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="grid grid-cols-2 gap-[3px] rounded-[10px] bg-[#EBE2D7] p-1">
                <Skeleton shape="cover" className="w-full" />
                <Skeleton shape="cover" className="w-full" />
              </div>
            ))}
          </div>
        </SkeletonGroup>
      </WizardShell>
    );
  }

  const headingLocked = (
    <div className="flex items-end justify-between gap-3">
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">
          Your {count} {count === 1 ? 'photo' : 'photos'}
        </h1>
        <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">We're turning each one into a coloring page.</p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1.5 rounded-2xl bg-[#F1ECE5] px-3 py-1.5 text-sm font-bold text-[#4A4446]">
        <Lock className="h-4 w-4" aria-hidden="true" />
        Locked
      </div>
    </div>
  );

  const headingOpen = (
    <div className="flex items-end justify-between gap-3">
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">
          {count >= 20 ? `Your ${count} photos` : 'Add 20 photos'}
        </h1>
        <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">Each one becomes a coloring page.</p>
      </div>
      <div
        className={`flex-shrink-0 rounded-2xl border bg-white px-3 py-1.5 text-[15px] font-bold ${
          count > 20 ? 'border-amber-300 text-amber-800' : 'border-md-pink-light-1 text-md-dark'
        }`}
      >
        {count}
        <span className="font-medium text-md-muted">
          {' '}/ 20<span className="hidden md:inline"> added</span>
        </span>
      </div>
    </div>
  );

  return (
    <>
      <WizardShell aside={sidePanel} bottomBar={bottomBar}>
        <div className="flex flex-col gap-3.5 md:gap-4">
          {/* Desktop shows a failed cover in the side panel instead (DeskPhotosCoverFailed). */}
          <div className={coverState === 'failed' ? 'lg:hidden' : undefined}>
            <CoverStatusStrip
              state={coverState}
              coverUrl={coverDisplayUrl}
              title={activeBook?.title}
              onView={() => setCoverSheetOpen(true)}
              onRetry={retryCover}
              onChangePhoto={goBackToCover}
              retrying={coverRetrying}
              usedUp={coverUsedUp}
            />
          </div>

          {isLineartLocked ? headingLocked : headingOpen}

          {isLineartLocked ? (
            <div role="status" className="flex flex-col gap-2.5 rounded-[14px] border border-md-pink-light-1 bg-white px-4 py-3.5">
              <div className="flex items-center gap-2.5">
                <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-md-pink-dark" aria-hidden="true" />
                <span className="flex-1 text-[15px] font-bold text-md-dark">Drawing your pages</span>
                <span className="text-sm font-bold text-md-dark">
                  {pagesStatus.done}
                  <span className="font-medium text-md-muted"> of {pagesStatus.total} done</span>
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-md-pink-light-3" aria-hidden="true">
                <div className="h-full bg-md-pink transition-[width] duration-500" style={{ width: `${pagesStatus.total ? Math.round((pagesStatus.done / pagesStatus.total) * 100) : 0}%` }} />
              </div>
              <span className="flex items-start gap-2 text-sm leading-snug text-[#4A4446]">
                <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-md-pink-dark" aria-hidden="true" />
                Your photos are locked while we draw. You can keep going and work on your cover and note.
              </span>
            </div>
          ) : (
            <>
              <div className="h-1.5 overflow-hidden rounded-full bg-md-pink-light-3" aria-hidden="true">
                <div className="h-full bg-md-pink transition-[width] duration-300" style={{ width: `${Math.min(100, Math.round((count / 20) * 100))}%` }} />
              </div>
              {photosBusy ? (
                // One card from picking until every photo is stored and prepared (HANDOFF.md, decided 2026-11-02).
                <div role="status" className="flex items-center gap-2.5 rounded-xl border border-md-pink-light-1 bg-white px-3 py-2.5 text-sm text-[#4A4446]">
                  <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-md-pink-dark" aria-hidden="true" />
                  {busyLabel}. This can take a moment.
                </div>
              ) : unsavedPhotos.length && !isLineartLocked ? (
                // TODO(design): not in docs/design; built from Preview's "We couldn't draw" note (GAPS.md).
                <div role="alert" className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm leading-snug text-red-900">
                  <AlertCircle className="mt-px h-[18px] w-[18px] flex-shrink-0 text-red-700" strokeWidth={2.2} aria-hidden="true" />
                  {unsavedAgain ? (
                    <span>
                      <strong>
                        We still couldn&rsquo;t save {unsavedPhotos.length === 1 ? '1 photo' : `${unsavedPhotos.length} photos`}
                      </strong>
                      , marked in red. Please remove {unsavedPhotos.length === 1 ? 'it' : 'them'}, or{' '}
                      <span className="lg:hidden">tap</span>
                      <span className="hidden lg:inline">click</span> {unsavedPhotos.length === 1 ? 'it' : 'each one'} and choose{' '}
                      <strong>Replace photo</strong>.
                    </span>
                  ) : (
                    <span>
                      <strong>
                        We couldn&rsquo;t save {unsavedPhotos.length === 1 ? '1 photo' : `${unsavedPhotos.length} photos`}
                      </strong>
                      , marked in red. Check your connection and <span className="lg:hidden">tap</span>
                      <span className="hidden lg:inline">click</span> Next again.
                    </span>
                  )}
                </div>
              ) : null}
              {count === 0 && !isLineartLocked ? (
                // PhotosEmpty: the tip under the counter.
                <div className="text-sm text-[#4A4446]">Pick several at once. You can crop and reorder them after.</div>
              ) : null}
              {count > 0 ? (
                <div className="text-sm text-[#4A4446]">
                  <span className="lg:hidden">Tap a photo to adjust it. Press and hold to move it.</span>
                  <span className="hidden lg:inline">Drag photos to reorder. Click a photo to adjust it. You can also drop photos on the Add photos tile.</span>
                </div>
              ) : null}
              <WorthALookNotice count={flaggedCount} />
            </>
          )}

          {count === 0 && !isLineartLocked ? (
            // PhotosEmpty (docs/design/HANDOFF.md): one big dashed box in place of the grid.
            <button
              type="button"
              onClick={openPicker}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                replaceTargetRef.current = null;
                uploaderRef.current?.addFiles(e.dataTransfer?.files);
              }}
              className="flex min-h-[200px] w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-md-control bg-[#FBF9F5] px-6 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink md:min-h-[260px] lg:min-h-[320px]"
            >
              <ImagePlus className="h-9 w-9 text-[#8A7F78]" aria-hidden="true" />
              <span className="text-base font-bold text-md-dark">Add your 20 photos</span>
              <span className="text-sm text-md-muted">
                <span className="lg:hidden">Tap to pick them from your camera roll. </span>
                <span className="hidden lg:inline">Drag photos here or click to browse. </span>
                Faces close up and bright light work best.
              </span>
            </button>
          ) : (
          <PhotoGrid
            images={images}
            locked={isLineartLocked}
            isDrawn={isDrawn}
            isPreparing={isPreparing}
            isUnsaved={isUnsaved}
            busy={photosBusy}
            onOpen={openTile}
            onRemove={removeWithUndo}
            onAdd={openPicker}
            onDropFiles={dropOnGrid}
            showAdd={!isLineartLocked}
            onReorder={(reordered) => {
              dispatch(setImages(reordered));
              onDragEnd(reordered);
            }}
          />
          )}

        </div>
      </WizardShell>

      {/* Hidden uploader: the picker and upload pipeline behind "Add photos". */}
      <FileUploader
        ref={uploaderRef}
        variant="picker"
        onChange={async (files) => {
          setPlacingPicked(true);
          try {
            await handleImageUpload(files);
          } finally {
            setPlacingPicked(false);
          }
        }}
        onBusy={(n) => {
          setUploaderBusy(n);
          if (n > 0) markPickerHeadsUpDone();
        }}
        single={false}
        bookId={activeBookId}
      />

      <PickerHeadsUp
        open={pickerHeadsUpOpen}
        device={applePickerDevice() || 'iphone'}
        accept={uploaderRef.current?.pickerAccept?.()}
        onFiles={addFromHeadsUp}
        onClose={() => setPickerHeadsUpOpen(false)}
      />

      <CoverReadySheet
        open={coverSheetOpen}
        onClose={() => setCoverSheetOpen(false)}
        coverUrl={coverDisplayUrl}
        title={activeBook?.title}
        onChangeCover={() => {
          setCoverSheetOpen(false);
          goBackToCover();
        }}
      />

      <PageCropScreen
        open={!!croppingImage}
        image={croppingImage}
        imageUrl={croppingImage?.blobUrl}
        cropData={croppingImage?.cropData}
        pageNumber={cropPageNumber}
        flagged={!!croppingImage?.cropReview?.flagged}
        onSave={handleCropSave}
        onClose={closeCrop}
        onReplace={replaceFromCrop}
      />

      {!!profile?.myBooks?.length && !activeBookId && !!profile?.data?.isOrderPlaces && (
        <BookSelectionModal
          isOpen={isOpenBookSelectionModal}
          setIsOpen={setIsOpenBookSelectionModal}
          books={profile.myBooks ?? []}
          onBookSelect={(bookId) => dispatch(setActiveBookId({ bookId }))}
        />
      )}

      {/* TODO(design): this pop-up isn't drawn (docs/design/GAPS.md). */}
      <Popup
        open={showDeleteFailed}
        onClose={() => setShowDeleteFailed(false)}
        tone="red"
        alert
        icon={<AlertCircle className="h-7 w-7" aria-hidden="true" />}
        title="We couldn't remove your photo"
        actions={[
          <Button
            key="retry"
            onClick={() => {
              setShowDeleteFailed(false);
              proceedWithGeneration(goOnAfterDeleteFailedRef.current || navigateToCoverStep);
            }}
          >
            Retry and Continue
          </Button>,
        ]}
      >
        Check your internet connection and try again.
      </Popup>
      {/* TODO(design): the too-many pop-up isn't drawn (docs/design/GAPS.md). The fewer-than-20 one is PopFewer. */}
      <Popup
        open={showImageLimitModal}
        onClose={() => setShowImageLimitModal(false)}
        icon={<Images className="h-7 w-7" aria-hidden="true" />}
        title="That's a few too many"
        actions={[
          <Button key="ok" onClick={() => setShowImageLimitModal(false)}>
            Got it
          </Button>,
        ]}
      >
        Your book holds 20 pages. Remove {Math.max(1, modalImageCount - 20)} {modalImageCount - 20 === 1 ? 'photo' : 'photos'} to continue.
      </Popup>
      <Popup
        open={showLessThan20Modal}
        onClose={() => {
          goOnAfterFewPhotosRef.current = null;
          setShowLessThan20Modal(false);
        }}
        icon={<ImagePlus className="h-7 w-7" aria-hidden="true" />}
        title={`Add ${Math.max(1, 20 - modalImageCount)} more ${20 - modalImageCount === 1 ? 'photo' : 'photos'}?`}
        actions={[
          <Button key="more" onClick={handleUploadMore}>
            Add more photos
          </Button>,
          <Button key="go" variant="secondary" onClick={handleContinue} disabled={photosBusy}>
            Continue with {modalImageCount}
          </Button>,
        ]}
      >
        Your book holds 20 pages. Add more to fill it, or continue and leave the extra pages blank for free coloring.
      </Popup>
    </>
  );
};

export default PlaceOrderContent;